/**
 * DashboardOrcamentosPage
 *
 * Dashboard com visão geral de orçamentos no padrão visual do dashboard financeiro.
 */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DollarSign,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Plus,
  FileText,
  LayoutList,
  CreditCard,
  RotateCcw
} from 'lucide-react'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { quoteService } from '../../../services/erpApi'
import KPICard from '../../../components/ERP/FinanceDashboard/KPICard/KPICard'
import './DashboardOrcamentosPage.css'

const STATUS_ITEMS = [
  { key: 'open', label: 'Abertos', className: 'status-open' },
  { key: 'approved', label: 'Aprovados', className: 'status-approved' },
  { key: 'expired', label: 'Vencidos', className: 'status-expired' },
  { key: 'cancelled', label: 'Cancelados', className: 'status-cancelled' },
  { key: 'converted', label: 'Convertidos', className: 'status-converted' }
]

function DashboardOrcamentosPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [stats, setStats] = useState({
    totalQuoted: 0,
    conversionRate: 0,
    approvedCount: 0,
    expiringCount: 0,
    expiringTotal: 0,
    stats: {
      open: 0,
      approved: 0,
      expired: 0,
      cancelled: 0,
      converted: 0
    }
  })
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const loadDashboardData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }

    try {
      const statsResponse = await quoteService.getDashboardStats()
      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadDashboardData()
  }, [])

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const shortcuts = useMemo(() => {
    const items = [
      {
        label: 'Novo Orçamento',
        icon: Plus,
        path: '/erp/orcamentos/novo',
        color: 'bg-emerald-600',
        module: MODULES.QUOTES,
        action: ACTIONS.CREATE
      },
      {
        label: 'Listagem',
        icon: LayoutList,
        path: '/erp/orcamentos',
        color: 'bg-blue-600',
        module: MODULES.QUOTES,
        action: ACTIONS.VIEW
      },
      {
        label: 'Pré-Venda',
        icon: CreditCard,
        path: '/erp/vendas/pre-venda',
        color: 'bg-indigo-600',
        module: MODULES.SALES,
        action: ACTIONS.SALES_PRESALE
      }
    ]

    return items.filter((item) => hasPermission(item.module, item.action))
  }, [hasPermission])

  const kpiData = [
    {
      label: 'Total Orçado',
      value: formatCurrency(stats.totalQuoted),
      change: 'Período atual',
      up: true,
      icon: DollarSign,
      color: 'text-emerald-500',
      bg: 'bg-emerald-50'
    },
    {
      label: 'Taxa de Conversão',
      value: `${stats.conversionRate.toFixed(1)}%`,
      change: 'Orçamentos convertidos',
      up: stats.conversionRate > 0,
      icon: TrendingUp,
      color: 'text-blue-500',
      bg: 'bg-blue-50'
    },
    {
      label: 'Orçamentos Aprovados',
      value: String(stats.approvedCount),
      change: 'Aguardando conversão',
      up: true,
      icon: CheckCircle,
      color: 'text-primary-500',
      bg: 'bg-primary-50'
    },
    {
      label: 'Vencendo em 7 dias',
      value: String(stats.expiringCount),
      change: formatCurrency(stats.expiringTotal),
      up: false,
      icon: AlertCircle,
      color: 'text-amber-500',
      bg: 'bg-amber-50'
    }
  ]

  if (loading) {
    return (
      <div className="quotes-dashboard-loading">
        <p>Carregando dados do dashboard...</p>
      </div>
    )
  }

  return (
    <div className={`quotes-dashboard${refreshing ? ' is-refreshing' : ''}`}>
      <div className="quotes-dashboard-header">
        <div>
          <h1 className="quotes-dashboard-title">
            Visão de <span className="quotes-dashboard-title-highlight">Orçamentos</span>
            {refreshing && (
              <RotateCcw size={20} className="quotes-dashboard-refresh-icon" aria-hidden />
            )}
          </h1>
          <p className="quotes-dashboard-subtitle">
            Indicadores e distribuição comercial Medik
          </p>
        </div>
        <div className="quotes-dashboard-header-actions">
          <button
            type="button"
            onClick={() => loadDashboardData(true)}
            className="quotes-dashboard-reload-btn"
            disabled={refreshing}
            aria-label="Atualizar dados do dashboard"
            title="Atualizar dados"
          >
            <RotateCcw
              size={18}
              className={refreshing ? 'quotes-dashboard-reload-icon is-spinning' : 'quotes-dashboard-reload-icon'}
            />
          </button>
          {hasPermission(MODULES.QUOTES, ACTIONS.CREATE) && (
            <button
              type="button"
              className="quotes-dashboard-new-btn"
              onClick={() => navigate('/erp/orcamentos/novo')}
            >
              <Plus size={16} />
              Novo Orçamento
            </button>
          )}
        </div>
      </div>

      {shortcuts.length > 0 && (
        <div className="quotes-dashboard-shortcuts-section">
          <h3 className="quotes-dashboard-section-title">Acesso Rápido</h3>
          <div className="quotes-dashboard-shortcuts-grid">
            {shortcuts.map((shortcut) => (
              <button
                key={shortcut.path}
                type="button"
                onClick={() => navigate(shortcut.path)}
                className="quotes-dashboard-shortcut-btn"
              >
                <div className={`quotes-dashboard-shortcut-icon ${shortcut.color}`}>
                  <shortcut.icon size={22} />
                </div>
                <span className="quotes-dashboard-shortcut-label">{shortcut.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="quotes-dashboard-dynamic">
        <div className="quotes-dashboard-kpi-grid">
          {kpiData.map((kpi) => (
            <KPICard
              key={kpi.label}
              label={kpi.label}
              value={kpi.value}
              change={kpi.change}
              up={kpi.up}
              icon={kpi.icon}
              color={kpi.color}
              bg={kpi.bg}
            />
          ))}
        </div>

        <div className="quotes-dashboard-status-card">
          <div className="quotes-dashboard-status-header">
            <h3 className="quotes-dashboard-status-title">Distribuição por Status</h3>
            <button
              type="button"
              className="quotes-dashboard-status-link"
              onClick={() => navigate('/erp/orcamentos')}
            >
              <FileText size={16} />
              Ver listagem
            </button>
          </div>

          <div className="quotes-dashboard-status-grid">
            {STATUS_ITEMS.map((item) => (
              <div key={item.key} className={`quotes-dashboard-status-item ${item.className}`}>
                <span className="quotes-dashboard-status-label">{item.label}</span>
                <span className="quotes-dashboard-status-value">{stats.stats[item.key] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardOrcamentosPage
